import Preloader from "components/Preloader/Preloader";
import {
  ProductImageContainer,
  Title,
} from "features/productDetails/ProductDetails";
import React from "react";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components/macro";
import { colors } from "theme/Theme";
import {
  fetchAccessoryById,
  selectAccessoryDetailsLoading,
  selectFetchedAccessory,
  setFetchedAccessory,
} from "./accessoryDetailsSlice";
import climaIMG from "../../assets/images/clima_img.png";
import { device } from "theme/Breakpoints";
import { DDSPrice } from "features/productDetails/components/PriceAndButtons";
import { SecondaryPrice, MainPrice } from "theme/GlobalElements";
import Button from "components/Button";
import { showRootModal } from "features/rootModal/rootModalSlice";
import { HiOutlineShoppingCart } from "react-icons/hi";
import { buttonType } from "components/Button/Button";
import { setBreadcrumbTitle } from "features/breadcrumb/breadcrumbSlice";
import FastOrder from "components/FastOrder";
import { PageWithFilterType } from "features/Filters/types";
import HeadMeta from "features/productDetails/components/HeadMeta";
import DualPrice, { EuroPrice } from "components/Price";

interface AccessoryDetailsProps {}

const AccessoryDetails: React.FC<AccessoryDetailsProps> = ({}) => {
  const dispatch = useDispatch();

  const accessory = useSelector(selectFetchedAccessory);
  const isLoading = useSelector(selectAccessoryDetailsLoading);

  const { id } = useParams();

  const addItem = () => {
    dispatch(
      showRootModal({
        modalType: "CHECKOUT",
        modalProps: {
          item: accessory,
          pageType: PageWithFilterType.accessories,
        },
      })
    );
  };

  useEffect(() => {
    dispatch(fetchAccessoryById(id));
    return () => {
      dispatch(setFetchedAccessory(null));
    };
  }, []);

  useEffect(() => {
    dispatch(setBreadcrumbTitle(accessory?.title));
    return () => {
      dispatch(setBreadcrumbTitle(""));
    };
  }, [accessory]);

  return isLoading ? (
    <Preloader />
  ) : accessory ? (
    <>
      <HeadMeta item={accessory} />
      <AccessoryDetailsContainer>
        <ProductImageContainer>
          <ProductImage
            src={`data:${accessory.image.contentType};base64,${accessory.image.image}`}
          />
        </ProductImageContainer>
        <StyledContentContainer>
          <TitleContainer>
            <Title className="text-md">{accessory?.title}</Title>
          </TitleContainer>
          <HorizontalLine />
          <AccessoryPrice>
            {accessory.price_promotion ? (
              <PriceContainer>
                <MainPrice className="text-lg main-price">
                  <DualPrice
                    bgn={accessory.price_promotion}
                    eur={accessory.price_promotion_eur}
                  />
                </MainPrice>
                <SecondaryPrice className="text-body secondary-price">
                  <EuroPrice bgn={accessory.price} eur={accessory.price_eur} />
                </SecondaryPrice>
              </PriceContainer>
            ) : (
              <PriceContainer>
                <MainPrice className="text-lg main-price">
                  <DualPrice bgn={accessory.price} eur={accessory.price_eur} />
                </MainPrice>               
              </PriceContainer>
            )}
            <DDSPrice className="text-xs">Цена с включено ДДС</DDSPrice>
            <HorizontalLine />
            <ButtonContainer>
              <Button
                onClick={addItem}
                title="Добави"
                type={buttonType.blueBtn}
                classNames={"product-details-button"}
                fontSizeClass="text-md"
              >
                <HiOutlineShoppingCart
                  style={{
                    width: 24,
                    height: 24,
                    color: "white",
                    marginRight: "5px",
                  }}
                />
              </Button>
            </ButtonContainer>
          </AccessoryPrice>
        </StyledContentContainer>
      </AccessoryDetailsContainer>
      <DescriptionContainer>
        <h3>Описание</h3>
        <div className="text-body">{accessory.description}</div>
      </DescriptionContainer>
      <FastOrder item={accessory} />
    </>
  ) : (
    <ErrorMessage>
      Възникна грешка със заявката ви! Моля опитайте по-късно!
    </ErrorMessage>
  );
};

const ProductImage = styled.img`
  width: 100%;
  max-width: 450px;
  height: 300px;
  background-repeat: no-repeat;
  background-size: cover;
  border: 1px solid ${colors.dark_gray};

  @media only screen and (${device.tablet}) {
    max-width: 100%;
    height: 100%;
  }
`;

const HorizontalLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.gray};
  margin: 20px 0;
  align-self: flex-start;
  justify-self: flex-start;
`;

const AccessoryDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  padding: 0 20px;

  @media only screen and (${device.tablet}) {
    flex-direction: row;
  }
`;

const TitleContainer = styled.div`
  flex: 1;
  align-items: center;
  margin-top: 10px;

  @media only screen and (${device.tablet}) {
    flex: 0 0 100%;
    margin-top: 0;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  width: 90%;
  padding: 10px 20px;
  color: black;
  background: ${colors.lightest_blue};
`;

const AccessoryPrice = styled.div`
  flex: 0 0 100%;
  display: flex;
  flex-flow: column;
  align-items: center;

  @media only screen and (${device.tablet}) {
    align-items: flex-start;
    margin: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;

  @media only screen and (${device.tablet}) {
    justify-content: flex-start;
  }
`;

const PriceContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;

  .main-price {
    order: 1;
    margin-right: 5px;
  }

  .secondary-price {
    order: 2;
  }

  @media only screen and (${device.tablet}) {
    justify-content: flex-start;
  }
`;

const StyledContentContainer = styled.div`
  justify-content: center;
  flex: 1;

  @media only screen and (${device.tablet}) {
    justify-content: flex-start;
  }
`;

const DescriptionContainer = styled.div`
  margin: 20px 10px;

  @media only screen and (${device.laptop}) {
    margin: 20px 20px;
  }
`;

export default AccessoryDetails;
